import { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Gavel,
  Radar,
  ListTodo,
  Bell,
  History,
  Puzzle,
  Server,
  ScrollText,
  ShieldAlert,
  DatabaseBackup,
  Brain,
  GitBranch,
  DollarSign,
  Sun,
  Moon,
  Monitor,
  PanelLeftClose,
  PanelLeft,
  UserCog,
  LogOut,
  Menu,
  ChevronDown,
  Link2,
  MessageSquare,
  Send,
  ArrowLeftRight,
  SearchX,
} from 'lucide-react';
import { useTheme } from '../hooks/useTheme';
import { useAlertSummary } from '../hooks/useAlertSummary';
import { pillTone, colorHex } from '../lib/format';
import { api } from '../lib/api';

async function handleLogout() {
  try {
    await api.post('/auth/logout');
  } catch {
    // ignore — clear locally regardless
  }
  localStorage.removeItem('jam_token');
  localStorage.removeItem('jam_user');
  window.location.href = '/login';
}

const navSections = [
  {
    title: 'Overview',
    items: [
      { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/command', icon: Radar, label: 'Command Center' },
    ],
  },
  {
    title: 'Analytics',
    items: [
      { to: '/pipeline', icon: GitBranch, label: 'Pipeline' },
      { to: '/quality', icon: Brain, label: 'AI Quality' },
      { to: '/cost', icon: DollarSign, label: 'Cost' },
    ],
  },
  {
    title: 'Operations',
    items: [
      { to: '/jobs', icon: ListTodo, label: 'Jobs' },
      { to: '/modules', icon: Puzzle, label: 'Modules' },
      { to: '/nodes', icon: Server, label: 'Nodes' },
    ],
  },
  /*
    MONITORING HOLDS EVERYTHING THAT WATCHES, in two tiers. The four flat items
    watch jam itself — true wherever it runs, whatever it is connected to. The
    subsections watch the sibling systems: Doc Activity reports on documents in
    Harpocrates, Judge Assignments on Citadel. Those two spent 2026-08-25 to
    08-27 as their own top-level sections so a reader scanning "is everything
    all right" would not trip over pages about somebody else's system — but
    they ARE monitoring, and top-level placement claimed they were something
    else. Nesting keeps both truths: they file under Monitoring because that is
    what they do, and each keeps its own system's heading because the two
    systems have different owners, databases, and failure modes — a paralegal
    asking about a client's DOCUMENTS still never reads past judge assignments
    to find them. One item per subsection is not a reason to flatten; the
    heading is where the next Harpocrates page already has a home.
  */
  {
    title: 'Monitoring',
    items: [
      { to: '/alerts', icon: Bell, label: 'Alerts' },
      { to: '/security', icon: ShieldAlert, label: 'Security' },
      { to: '/backups', icon: DatabaseBackup, label: 'Backups' },
      { to: '/logs', icon: ScrollText, label: 'Logs' },
    ],
    subsections: [
      {
        title: 'Harpocrates',
        items: [
          { to: '/activity', icon: History, label: 'Doc Activity' },
          { to: '/atlas-citadel-sync', icon: ArrowLeftRight, label: 'ATLAS ↔ Citadel' },
          { to: '/missing-from-atlas', icon: SearchX, label: 'Missing from ATLAS' },
        ],
      },
      {
        title: 'Citadel',
        items: [
          { to: '/judge-assignments', icon: Gavel, label: 'Judge Assignments' },
          { to: '/cross-references', icon: Link2, label: 'Cross-References' },
          { to: '/sms-evidence', icon: MessageSquare, label: 'SMS Evidence' },
          { to: '/send-ledger', icon: Send, label: 'Send Ledger' },
        ],
      },
    ],
  },
];

type NavItem = (typeof navSections)[number]['items'][number];
type NavSubsection = { title: string; items: NavItem[] };
/** Fold-state key for a subsection — namespaced so it can never collide with a
 *  top-level section title. */
const subKey = (section: string, sub: string) => `${section}/${sub}`;
const sectionSubs = (s: (typeof navSections)[number]): NavSubsection[] =>
  ('subsections' in s ? (s.subsections as NavSubsection[]) : undefined) ?? [];
/** Every item in a section, subsections included — route lookups care about
 *  membership, not depth. */
const allItems = (s: (typeof navSections)[number]): NavItem[] => [
  ...s.items,
  ...sectionSubs(s).flatMap((sub) => sub.items),
];

export function Layout() {
  const { theme, setTheme } = useTheme();
  const { pathname } = useLocation();
  const alertSummary = useAlertSummary();
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('jam_sidebar');
    if (saved) return saved === 'collapsed';
    return window.innerWidth < 768;
  });
  // Off-canvas drawer state (mobile only; the inline sidebar handles md+).
  const [mobileOpen, setMobileOpen] = useState(false);
  // Which nav sections the user has folded shut. Persisted, because a sidebar
  // that forgets what you closed teaches you to stop closing things.
  const [closedSections, setClosedSections] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('jam_nav_closed') ?? '[]') as string[]);
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    localStorage.setItem('jam_sidebar', collapsed ? 'collapsed' : 'expanded');
  }, [collapsed]);

  useEffect(() => {
    localStorage.setItem('jam_nav_closed', JSON.stringify([...closedSections]));
  }, [closedSections]);

  // Close the mobile drawer whenever the route changes — and make sure every
  // fold above the new route is open: the page you are ON must never be hidden,
  // whether its fold is a section or a subsection inside one.
  useEffect(() => {
    setMobileOpen(false);
    const matches = (to: string) => (to === '/' ? pathname === '/' : pathname.startsWith(to));
    const keysToOpen: string[] = [];
    for (const s of navSections) {
      if (allItems(s).some(({ to }) => matches(to))) keysToOpen.push(s.title);
      for (const sub of sectionSubs(s)) {
        if (sub.items.some(({ to }) => matches(to))) keysToOpen.push(subKey(s.title, sub.title));
      }
    }
    if (keysToOpen.some((k) => closedSections.has(k))) {
      setClosedSections((prev) => {
        const next = new Set(prev);
        for (const k of keysToOpen) next.delete(k);
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const toggleSection = (title: string) =>
    setClosedSections((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });

  // One item row, at either nesting depth. A plain function, not a component:
  // it closes over `collapsed`/`alertSummary` and has no state of its own, and
  // a component type recreated every render would remount its subtree.
  const renderItem = ({ to, icon: Icon, label }: NavItem, deep: boolean) => {
    // Live unacknowledged-alert backlog surfaced on the Alerts item.
    const showBadge = to === '/alerts' && alertSummary.count > 0;
    return (
      <NavLink
        key={to}
        to={to}
        end={to === '/'}
        title={showBadge ? `${label} — ${alertSummary.count} unacknowledged` : label}
        className={({ isActive }) =>
          `relative flex items-center gap-3 ${deep ? 'pl-10' : 'pl-7'} pr-4 py-2 min-h-[44px] md:min-h-0 text-xs transition-colors ${
            collapsed ? 'md:pl-4' : ''
          } ${
            isActive
              ? 'text-accent border-l-2 border-accent bg-accent/5'
              : 'text-text-secondary hover:text-text-primary border-l-2 border-transparent'
          }`
        }
      >
        <span className="relative shrink-0">
          <Icon size={16} className="shrink-0" />
          {/* Collapsed desktop hides the label + count, so surface the
              backlog as a severity-colored dot on the icon instead. */}
          {showBadge && (
            <span
              className={collapsed ? 'hidden md:block' : 'hidden'}
              style={{
                position: 'absolute',
                top: -2,
                right: -2,
                width: 7,
                height: 7,
                background: colorHex(alertSummary.worst),
              }}
            />
          )}
        </span>
        {/* Label hides only on collapsed desktop; always shown in the drawer. */}
        <span className={collapsed ? 'md:hidden' : ''}>{label}</span>
        {/* Expanded sidebar + mobile drawer: full count badge, tinted by
            the worst unacknowledged severity. */}
        {showBadge && (
          <span
            className={`pill pill-${pillTone(alertSummary.worst)} ml-auto ${
              collapsed ? 'md:hidden' : ''
            }`}
          >
            {alertSummary.count > 99 ? '99+' : alertSummary.count}
          </span>
        )}
      </NavLink>
    );
  };

  const themeIcon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Monitor;
  const ThemeIcon = themeIcon;

  const cycleTheme = () => {
    const next: Record<string, 'light' | 'dark' | 'system'> = {
      dark: 'light',
      light: 'system',
      system: 'dark',
    };
    setTheme(next[theme]);
  };

  // The Command Center is a full-bleed ops wall; Logs is a full-height tail.
  // Everything else scrolls inside a padded, max-width column.
  const mode: 'bleed' | 'fullpad' | 'pad' =
    pathname === '/command' ? 'bleed' : pathname === '/logs' ? 'fullpad' : 'pad';

  return (
    <div className="h-dvh flex flex-col">
      <nav className="h-[49px] z-50 bg-surface border-b border-border flex items-center pad-safe-x justify-between shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden -ml-3 min-w-[44px] min-h-[44px] inline-flex items-center justify-center text-text-muted hover:text-text-primary transition-colors"
            title="Menu"
            aria-label="Toggle navigation"
          >
            <Menu size={18} />
          </button>
          <span className="text-accent font-bold text-sm tracking-wider">JAM</span>
          <span className="text-text-muted text-xs hidden sm:inline">Admin Dashboard</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-text-muted text-xs hidden sm:inline">
            {localStorage.getItem('jam_user') ?? 'admin'}
          </span>
          <NavLink
            to="/account"
            className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center text-text-muted hover:text-text-primary transition-colors"
            title="Account"
          >
            <UserCog size={16} />
          </NavLink>
          <button
            onClick={cycleTheme}
            className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center text-text-muted hover:text-text-primary transition-colors"
            title={`Theme: ${theme}`}
          >
            <ThemeIcon size={16} />
          </button>
          <button
            onClick={handleLogout}
            className="hidden md:inline-flex min-w-[44px] min-h-[44px] items-center justify-center text-text-muted hover:text-accent-red transition-colors"
            title="Log out"
          >
            <LogOut size={16} />
          </button>
        </div>
      </nav>

      <div className="flex flex-1 min-h-0">
        {/* Backdrop behind the mobile drawer */}
        {mobileOpen && (
          <div
            onClick={() => setMobileOpen(false)}
            className="fixed inset-0 top-[49px] z-40 bg-black/50 md:hidden"
            aria-hidden
          />
        )}
        <aside
          className={`fixed top-[49px] bottom-0 left-0 z-50 w-64 transform transition-transform duration-200 md:static md:top-auto md:bottom-auto md:z-auto md:translate-x-0 md:transition-all ${
            mobileOpen ? 'translate-x-0' : '-translate-x-full'
          } ${collapsed ? 'md:w-14' : 'md:w-48'} border-r border-border bg-surface flex flex-col shrink-0 pb-[env(safe-area-inset-bottom)] md:pb-0`}
        >
          <div className="flex-1 py-2 overflow-y-auto">
            {navSections.map((section, si) => {
              const sectionClosed = closedSections.has(section.title);
              return (
                <div key={section.title}>
                  {/* Section header: a fold toggle on mobile + expanded desktop;
                    on collapsed desktop a divider stands in instead (the icon
                    rail ignores folds — hiding icons there would just be a
                    mystery gap). */}
                  <button
                    onClick={() => toggleSection(section.title)}
                    className={`w-full flex items-center gap-1.5 px-4 pt-3 pb-1 min-h-[44px] md:min-h-0 text-[11px] md:text-[10px] font-semibold uppercase tracking-wider text-text-muted/60 hover:text-text-muted transition-colors ${
                      collapsed ? 'md:hidden' : ''
                    }`}
                    aria-expanded={!sectionClosed}
                  >
                    <ChevronDown
                      size={11}
                      className={`shrink-0 transition-transform ${sectionClosed ? '-rotate-90' : ''}`}
                    />
                    {section.title}
                  </button>
                  {collapsed && si > 0 && (
                    <div className="hidden md:block mx-3 my-2 border-t border-border" />
                  )}
                  {/* Folded: hidden where the header shows; still rendered for
                    the desktop icon rail, which has no headers to fold. */}
                  <div className={sectionClosed ? (collapsed ? 'hidden md:block' : 'hidden') : ''}>
                    {section.items.map((item) => renderItem(item, false))}
                    {sectionSubs(section).map((sub) => {
                      const key = subKey(section.title, sub.title);
                      const subClosed = closedSections.has(key);
                      return (
                        <div key={key}>
                          <button
                            onClick={() => toggleSection(key)}
                            className={`w-full flex items-center gap-1.5 pl-7 pr-4 pt-2 pb-1 min-h-[44px] md:min-h-0 text-[11px] md:text-[10px] font-semibold uppercase tracking-wider text-text-muted/50 hover:text-text-muted transition-colors ${
                              collapsed ? 'md:hidden' : ''
                            }`}
                            aria-expanded={!subClosed}
                          >
                            <ChevronDown
                              size={10}
                              className={`shrink-0 transition-transform ${subClosed ? '-rotate-90' : ''}`}
                            />
                            {sub.title}
                          </button>
                          <div
                            className={subClosed ? (collapsed ? 'hidden md:block' : 'hidden') : ''}
                          >
                            {sub.items.map((item) => renderItem(item, true))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Logout lives down here on phones — see the top-bar button. */}
          <button
            onClick={handleLogout}
            className="md:hidden flex items-center gap-3 pl-7 pr-4 min-h-[44px] text-xs text-text-secondary hover:text-accent-red border-t border-border transition-colors"
          >
            <LogOut size={16} className="shrink-0" />
            Log out
          </button>
          {/* Collapse toggle is a desktop affordance only. */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden md:block p-3 text-text-muted hover:text-text-primary border-t border-border transition-colors"
          >
            {collapsed ? <PanelLeft size={16} /> : <PanelLeftClose size={16} />}
          </button>
        </aside>

        {/* min-w-0: flex items default to min-width:auto, which lets wide page
            content stretch the viewport instead of shrinking — the page would
            scroll horizontally on phones. Pages opt into horizontal scrolling
            only via local .scroll-x wrappers. */}
        <main className="flex-1 min-h-0 min-w-0 flex flex-col pad-safe">
          {mode === 'bleed' ? (
            <div className="flex-1 min-h-0 min-w-0 overflow-x-hidden">
              <Outlet />
            </div>
          ) : mode === 'fullpad' ? (
            <div className="flex-1 min-h-0 min-w-0 overflow-x-hidden p-3 sm:p-6 flex flex-col">
              <Outlet />
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-3 sm:p-6">
              <div className="max-w-content mx-auto">
                <Outlet />
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
